import { useEffect, useRef, useState } from 'react';
import { Button, Card, Dialog, IconAction, InfoTip, Input, Select, TextField, TextSkeleton, ErrorState, cn } from '@dru/ui';
import { Archive, Check, ChevronDown, MoreHorizontal, Pencil, Plus, RotateCcw, Search, X } from 'lucide-react';
import { ApiError } from '@dru/api-client';
import { api } from '../../../lib/api';
import { useQuery } from '../../../lib/useQuery';
import { usePages } from '../../../lib/usePages';
import { errorMessage } from '../../../lib/auth';
import { PageHeader } from '../../../layouts/ConsoleShell';
import { forgetSuggestions } from '../../../lib/useSuggestions';

type Status = 'PENDING' | 'APPROVED' | 'REJECTED' | 'RETIRED';
interface Row {
  id: string; list: string; text: string; isActive: boolean; status: Status; source: 'SEEDED' | 'TYPED';
  usageCount: number; createdAt: string; reviewedAt: string | null; reviewNote: string | null;
}
interface ListInfo { code: string; label: string; counts: Record<Status, number> }
interface View { lists: ListInfo[]; items: Row[]; nextCursor: string | null }

const INTRO = 'Ready answers offered under the free-text fields at a station. Phrases typed by staff wait here for review.';
const HINT = 'A typed phrase is offered to nobody until it is approved here, because free text can carry one '
  + 'patient\'s details or a misspelling. Approve only general wording. Retiring or rejecting a phrase never '
  + 'changes what is already recorded on an examination.';

const added = (iso: string) => new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

type Shelf = 'RETIRED' | 'REJECTED' | null;

/**
 * The suggestion lists behind the free-text clinical fields, and their
 * review. Learned phrases wait in a "To review" strip until an administrator
 * approves (or rejects) them; approved phrases are shown as tags, the way a
 * reader takes in a vocabulary at a glance; retired and rejected ones sit
 * behind two small links underneath. One list at a time.
 */
export function Suggestions({ embedded = false }: { embedded?: boolean } = {}) {
  const meta = useQuery<View>(() => api.get('/clinical-suggestions/all?limit=1'), []);
  const [list, setList] = useState<string>('COMPLAINT');
  const [q, setQ] = useState('');
  const [term, setTerm] = useState('');
  useEffect(() => { const t = setTimeout(() => setTerm(q.trim()), 250); return () => clearTimeout(t); }, [q]);
  const [shelf, setShelf] = useState<Shelf>(null);

  // Open where the work is: the first list with phrases to review. Once.
  const [placed, setPlaced] = useState(false);
  useEffect(() => {
    if (placed || !meta.data) return;
    const waiting = meta.data.lists.find((l) => l.counts.PENDING > 0);
    setList(waiting?.code ?? meta.data.lists[0]?.code ?? 'COMPLAINT');
    setPlaced(true);
  }, [meta.data, placed]);

  const never = <T,>() => new Promise<T>(() => {});
  const query = (status: Status, cursor?: string | null) => api.get<View>(
    `/clinical-suggestions/all?list=${list}&status=${status}&limit=200`
    + (term ? `&q=${encodeURIComponent(term)}` : '') + (cursor ? `&cursor=${cursor}` : ''));
  const offered = usePages<Row>((c) => (placed ? query('APPROVED', c) : never<View>()), [list, term, placed]);
  const pending = usePages<Row>((c) => (placed ? query('PENDING', c) : never<View>()), [list, term, placed]);
  const shelved = usePages<Row>((c) => (placed && shelf ? query(shelf, c) : Promise.resolve({ lists: [], items: [], nextCursor: null })), [list, term, placed, shelf]);
  const exactQ = useQuery<View | null>(
    () => (term ? api.get<View>(`/clinical-suggestions/all?list=${list}&q=${encodeURIComponent(term)}&limit=20`) : Promise.resolve(null)),
    [list, term],
  );

  const [undo, setUndo] = useState<Row | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<Row | null>(null);
  const [editing, setEditing] = useState<Row | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  useEffect(() => { setPicked(new Set()); }, [list, term]);

  const lists = meta.data?.lists ?? [];
  const info = lists.find((l) => l.code === list);
  const label = info?.label ?? list;
  const counts = info?.counts;
  const waitingTotal = lists.reduce((n, l) => n + l.counts.PENDING, 0);

  const refresh = () => { forgetSuggestions(list); offered.reload(); pending.reload(); shelved.reload(); meta.reload(); exactQ.reload(); };
  async function act(key: string, fn: () => Promise<unknown>) {
    setBusy(key); setError(null);
    try { await fn(); refresh(); } catch (e) { setError(errorMessage(e)); } finally { setBusy(null); }
  }
  const move = (r: Row, m: 'approve' | 'reject' | 'retire' | 'restore', note?: string) =>
    api.post<Row>(`/clinical-suggestions/${r.id}/${m}`, note ? { note } : {});

  const typedText = q.trim();
  const exact = typedText
    ? (exactQ.data?.items ?? []).find((r) => r.text.trim().toLowerCase() === typedText.toLowerCase())
    : undefined;
  const add = () => {
    if (!typedText || exact || term !== typedText) return;
    void act('add', async () => { await api.post('/clinical-suggestions', { list, text: typedText }, crypto.randomUUID()); setQ(''); });
  };
  const retire = (r: Row) => void act(r.id, async () => { await move(r, 'retire'); setUndo(r); });
  const restore = (r: Row) => void act(r.id, async () => { await move(r, 'restore'); setUndo((u) => (u?.id === r.id ? null : u)); });
  const approve = (r: Row) => void act(r.id, () => move(r, 'approve'));
  const approvePicked = () => void act('bulk', async () => {
    for (const r of pending.items.filter((x) => picked.has(x.id))) {
      try { await move(r, 'approve'); } catch (e) { throw new Error(`Stopped at “${r.text}”: ${errorMessage(e)}`); }
    }
    setPicked(new Set());
  });
  const choose = (code: string) => { setList(code); setQ(''); setTerm(''); setError(null); setUndo(null); setShelf(null); };
  const toggle = (id: string) => setPicked((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const allPicked = pending.items.length > 0 && pending.items.every((r) => picked.has(r.id));

  if (meta.error) return <ErrorState title="Could not load the suggestion lists" detail={meta.error} onRetry={meta.reload} />;

  const intro = (
    <p className="flex items-start gap-1.5 text-small text-neutral-600">
      <span>{INTRO}</span>
      <InfoTip label="About suggestion review">{HINT}</InfoTip>
    </p>
  );

  return (
    <div className="space-y-4">
      {embedded ? intro : <PageHeader title="Suggestion lists" hint={`${INTRO} ${HINT}`} />}

      {meta.loading && !meta.data ? <TextSkeleton lines={6} /> : (
        <div className="grid items-start gap-5 lg:grid-cols-[16rem_minmax(0,1fr)]">
          <label className="flex flex-col gap-1 lg:hidden">
            <span className="text-caption font-medium text-neutral-700">List</span>
            <Select value={list} onChange={(e) => choose(e.target.value)} aria-label="Suggestion list">
              {lists.map((l) => (
                <option key={l.code} value={l.code}>
                  {l.label} ({l.counts.APPROVED}{l.counts.PENDING ? ` · ${l.counts.PENDING} to review` : ''})
                </option>
              ))}
            </Select>
          </label>
          <Card className="hidden overflow-hidden lg:block">
            <nav aria-label="Suggestion lists">
              <div className="flex items-baseline justify-between border-b border-neutral-100 px-4 py-3">
                <h2 className="text-small font-semibold text-neutral-900">Lists</h2>
                <span className="text-caption text-neutral-500">offered</span>
              </div>
              {waitingTotal > 0 && (
                <p className="flex items-center gap-2 border-b border-neutral-100 bg-dueSoon-100 px-4 py-2 text-caption font-medium text-dueSoon-600">
                  <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-dueSoon-600" />
                  {waitingTotal} phrase{waitingTotal === 1 ? '' : 's'} to review
                </p>
              )}
              <ul className="py-1.5">
                {lists.map((l) => {
                  const on = l.code === list;
                  return (
                    <li key={l.code}>
                      <button
                        type="button" aria-current={on ? 'true' : undefined} onClick={() => choose(l.code)}
                        className={cn(
                          'relative flex min-h-10 w-full items-center justify-between gap-2 px-4 text-left text-small transition-colors duration-hover',
                          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500',
                          on ? 'bg-primary-50 font-medium text-primary-800' : 'text-neutral-700 hover:bg-neutral-50 hover:text-neutral-900',
                        )}
                      >
                        {/* The selected list is marked by a bar, as in the main sidebar. */}
                        {on && <span aria-hidden className="absolute inset-y-1.5 left-0 w-[3px] rounded-r bg-primary-600" />}
                        <span className="min-w-0 truncate">{l.label}</span>
                        <span className="flex shrink-0 items-center gap-1">
                          {l.counts.PENDING > 0 && (
                            <span className="rounded-full bg-dueSoon-100 px-1.5 text-caption font-semibold tabular-nums text-dueSoon-600"
                                  title={`${l.counts.PENDING} to review`}>
                              {l.counts.PENDING} new
                            </span>
                          )}
                          <span className={cn('min-w-7 rounded-full px-1.5 text-center text-caption tabular-nums',
                            on ? 'bg-primary-100 text-primary-700' : 'bg-neutral-100 text-neutral-600')}
                                title={`${l.counts.APPROVED} offered`}>
                            {l.counts.APPROVED}
                          </span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </nav>
          </Card>

          <Card className="min-w-0">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-3 px-5 pb-4 pt-4">
              <div className="min-w-0 flex-1">
                <h2 className="text-h4 text-neutral-900">{label}</h2>
                {counts && <p className="text-small text-neutral-600"><span className="tabular-nums">{counts.APPROVED}</span> offered at the stations</p>}
              </div>
              <div className="relative w-full sm:w-72">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" aria-hidden />
                <Input
                  value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find or add a phrase"
                  aria-label={`Find or add a phrase in ${label}`} className="h-10 pl-9"
                  onKeyDown={(e) => { if (e.key === 'Enter') add(); if (e.key === 'Escape') setQ(''); }}
                />
              </div>
            </div>

            {(typedText && term === typedText && !exactQ.loading) || undo || error ? (
              <div className="space-y-2 px-5 pb-4">
                {typedText && term === typedText && !exactQ.loading && (
                  <div aria-live="polite" className="flex flex-wrap items-center gap-2 text-small">
                    {!exact ? (
                      <Button size="sm" loading={busy === 'add'} onClick={add} icon={<Plus className="h-4 w-4" aria-hidden />}>
                        Add “{typedText}” to {label.toLowerCase()}
                      </Button>
                    ) : exact.status === 'APPROVED' ? (
                      <span className="text-neutral-600">“{exact.text}” is already offered.</span>
                    ) : exact.status === 'PENDING' ? (
                      <><span className="text-neutral-600">“{exact.text}” is waiting for review.</span>
                        <Button size="sm" loading={busy === exact.id} onClick={() => approve(exact)} icon={<Check className="h-4 w-4" aria-hidden />}>Approve it</Button></>
                    ) : (
                      <><span className="text-neutral-600">“{exact.text}” was {exact.status === 'RETIRED' ? 'retired' : 'rejected'}.</span>
                        <Button size="sm" variant="secondary" loading={busy === exact.id}
                                onClick={() => (exact.status === 'RETIRED' ? restore(exact) : approve(exact))}
                                icon={<RotateCcw className="h-4 w-4" aria-hidden />}>Offer it again</Button></>
                    )}
                  </div>
                )}
                {undo && (
                  <div role="status" className="flex flex-wrap items-center gap-2 rounded-input bg-neutral-100 px-3 py-2 text-small text-neutral-700">
                    <span className="min-w-0 flex-1">Retired “{undo.text}”. It is no longer offered; nothing already recorded changes.</span>
                    <Button size="sm" variant="ghost" loading={busy === undo.id} onClick={() => restore(undo)}>Undo</Button>
                  </div>
                )}
                {error && <p role="alert" className="text-caption text-danger-600">{error}</p>}
              </div>
            ) : null}

            {/* To review: only when something waits. Rows, because each needs a decision. */}
            {pending.items.length > 0 && (
              <section aria-labelledby="review-h" className="mx-5 mb-4 overflow-hidden rounded-card border border-dueSoon-600 bg-dueSoon-100">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5">
                  <h3 id="review-h" className="text-small font-semibold text-dueSoon-600">
                    To review <span className="tabular-nums">({counts?.PENDING ?? pending.items.length})</span>
                  </h3>
                  <span className="min-w-0 flex-1 text-caption text-neutral-700">Approve only general wording — never anything that names or describes one person.</span>
                  {pending.items.length > 1 && (
                    <label className="inline-flex items-center gap-1.5 text-caption text-neutral-700 max-md:min-h-10">
                      <input type="checkbox" className="h-4 w-4 accent-primary-600" checked={allPicked}
                             onChange={() => setPicked(allPicked ? new Set() : new Set(pending.items.map((r) => r.id)))} />
                      Select all
                    </label>
                  )}
                  {picked.size > 0 && (
                    <Button size="sm" loading={busy === 'bulk'} onClick={approvePicked} icon={<Check className="h-4 w-4" aria-hidden />}>
                      Approve {picked.size}
                    </Button>
                  )}
                </div>
                <ul className="divide-y divide-neutral-100 bg-white">
                  {pending.items.map((r) => (
                    <li key={r.id} className="flex items-center gap-3 px-4 py-2">
                      {pending.items.length > 1 && (
                        <input type="checkbox" className="h-4 w-4 shrink-0 accent-primary-600" checked={picked.has(r.id)}
                               onChange={() => toggle(r.id)} aria-label={`Select “${r.text}”`} />
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="break-words text-body text-neutral-900">“{r.text}”</p>
                        <p className="text-caption text-neutral-500">
                          Typed by staff · added {added(r.createdAt)}{r.usageCount > 0 ? ` · used ${r.usageCount} time${r.usageCount === 1 ? '' : 's'}` : ''}
                        </p>
                      </div>
                      <Button size="sm" loading={busy === r.id} onClick={() => approve(r)}>Approve</Button>
                      <IconAction label={`Edit “${r.text}” before approving`} icon={Pencil} onClick={() => setEditing(r)} />
                      <IconAction label={`Reject “${r.text}”`} icon={X} onClick={() => setRejecting(r)} />
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Offered: tags. */}
            <div className="px-5 pb-5">
              {offered.error ? <ErrorState title="Could not load the phrases" detail={offered.error} onRetry={offered.reload} />
                : offered.loading || !placed ? <TextSkeleton lines={3} />
                : offered.items.length === 0 ? (
                  <p className="rounded-card border border-dashed border-neutral-300 px-4 py-8 text-center text-small text-neutral-500">
                    {term ? `No offered phrase matches “${term}”.` : 'Nothing offered on this list yet. Type the first phrase above.'}
                  </p>
                ) : (
                  <ul aria-label={`Phrases offered in ${label}`} className="flex flex-wrap gap-2">
                    {offered.items.map((r) => (
                      <Tag key={r.id} row={r} busy={busy === r.id}
                           actions={[
                             { label: 'Edit wording', icon: Pencil, run: () => setEditing(r) },
                             { label: 'Retire — stop offering', icon: Archive, run: () => retire(r) },
                           ]} />
                    ))}
                  </ul>
                )}
              {offered.hasMore && !offered.loading && (
                <Button className="mt-3" variant="ghost" size="sm" loading={offered.loadingMore} onClick={() => void offered.loadMore()}
                        icon={<ChevronDown className="h-4 w-4" aria-hidden />}>Show more</Button>
              )}

              {/* Retired and rejected: out of the way, one click to see. */}
              {counts && (counts.RETIRED > 0 || counts.REJECTED > 0) && (
                <div className="mt-5 border-t border-neutral-100 pt-3">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-small">
                    {(['RETIRED', 'REJECTED'] as const).filter((st) => counts[st] > 0).map((st) => (
                      <button key={st} type="button" aria-expanded={shelf === st} onClick={() => setShelf(shelf === st ? null : st)}
                              className="inline-flex min-h-8 items-center gap-1 font-medium text-primary-600 hover:text-primary-700 max-md:min-h-10">
                        <ChevronDown aria-hidden className={cn('h-4 w-4 transition-transform', shelf === st && 'rotate-180')} />
                        {st === 'RETIRED' ? 'Retired' : 'Rejected'} ({counts[st]})
                      </button>
                    ))}
                  </div>
                  {shelf && (
                    <div className="mt-3">
                      {shelved.loading ? <TextSkeleton lines={2} /> : (
                        <ul className="flex flex-wrap gap-2">
                          {shelved.items.map((r) => (
                            <Tag key={r.id} row={r} muted busy={busy === r.id}
                                 actions={[shelf === 'RETIRED'
                                   ? { label: 'Offer again', icon: RotateCcw, run: () => restore(r) }
                                   : { label: 'Approve after all', icon: Check, run: () => approve(r) }]} />
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </Card>
        </div>
      )}

      {rejecting && (
        <RejectDialog row={rejecting} onClose={() => setRejecting(null)}
          onReject={(note) => move(rejecting, 'reject', note).then(() => { setRejecting(null); refresh(); })} />
      )}
      {editing && (
        <EditDialog row={editing} onClose={() => setEditing(null)} onDone={() => { setEditing(null); refresh(); }} />
      )}
    </div>
  );
}

/**
 * One phrase as a tag, with its actions behind "⋯". How often staff picked
 * it rides along as a small count; a phrase staff typed carries a dot.
 */
function Tag({ row: r, actions, muted, busy }: {
  row: Row; muted?: boolean; busy?: boolean;
  actions: Array<{ label: string; icon: typeof Pencil; run: () => void }>;
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLLIElement>(null);
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => { if (!root.current?.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', away); document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', away); document.removeEventListener('keydown', esc); };
  }, [open]);
  const title = [r.source === 'TYPED' ? 'Typed by staff' : null, r.usageCount > 0 ? `used ${r.usageCount} times` : null, r.reviewNote]
    .filter(Boolean).join(' · ') || undefined;
  return (
    <li ref={root} className="relative">
      <span title={title} className={cn(
        'inline-flex min-h-9 items-center gap-1.5 rounded-full border py-1 pl-3.5 pr-1 text-small transition-colors duration-hover max-md:min-h-10',
        muted ? 'border-dashed border-neutral-300 bg-white text-neutral-500'
          : open ? 'border-primary-300 bg-primary-50 text-neutral-900'
            : 'border-neutral-200 bg-neutral-50 text-neutral-900 hover:border-neutral-300 hover:bg-white',
      )}>
        {r.source === 'TYPED' && <span aria-label="Typed by staff" className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary-500" />}
        <span className={cn('break-words', muted && 'line-through decoration-neutral-300')}>{r.text}</span>
        {r.usageCount > 0 && <span className="text-caption tabular-nums text-neutral-500">{r.usageCount}</span>}
        <button type="button" aria-haspopup="menu" aria-expanded={open} aria-label={`Actions for “${r.text}”`}
                onClick={() => setOpen((o) => !o)} disabled={busy}
                className="inline-flex h-7 w-7 items-center justify-center rounded-full text-neutral-500 hover:bg-neutral-200 hover:text-neutral-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:opacity-50 max-md:h-8 max-md:w-8">
          <MoreHorizontal className="h-4 w-4" aria-hidden />
        </button>
      </span>
      {open && (
        <div role="menu" className="absolute left-0 top-full z-20 mt-1 min-w-48 overflow-hidden rounded-card border border-neutral-200 bg-white py-1 shadow-card">
          {actions.map((a) => (
            <button key={a.label} type="button" role="menuitem" onClick={() => { setOpen(false); a.run(); }}
                    className="flex min-h-9 w-full items-center gap-2 px-3 text-left text-small text-neutral-800 hover:bg-neutral-50 focus-visible:bg-neutral-50 focus-visible:outline-none max-md:min-h-10">
              <a.icon className="h-4 w-4 text-neutral-500" aria-hidden />{a.label}
            </button>
          ))}
        </div>
      )}
    </li>
  );
}

function RejectDialog({ row, onClose, onReject }: { row: Row; onClose: () => void; onReject: (note: string) => Promise<unknown> }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const go = async () => {
    setBusy(true); setError(null);
    try { await onReject(note.trim()); } catch (e) { setError(errorMessage(e)); setBusy(false); }
  };
  return (
    <Dialog title={`Reject “${row.text}”`} onClose={onClose}
      footer={<><Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="danger" loading={busy} onClick={() => void go()}>Reject</Button></>}>
      <div className="space-y-3">
        <p className="text-small text-neutral-700">It will never be offered, even if typed again. It stays on record, and the examination it was typed on is not changed.</p>
        <TextField label="Reason (optional)" value={note} onChange={setNote} placeholder="Names a patient, misspelt, too specific…" />
        {error && <p role="alert" className="text-caption text-danger-600">{error}</p>}
      </div>
    </Dialog>
  );
}

/**
 * Reword an entry. A wording that lands on another entry's spelling is
 * refused by the server; then the same dialog offers to merge the two.
 */
function EditDialog({ row, onClose, onDone }: { row: Row; onClose: () => void; onDone: () => void }) {
  const [text, setText] = useState(row.text);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [twin, setTwin] = useState<Row | null>(null);
  const pending = row.status === 'PENDING';
  const save = async (andApprove: boolean) => {
    setBusy(true); setError(null); setTwin(null);
    try {
      if (text.trim() !== row.text) await api.patch(`/clinical-suggestions/${row.id}`, { text: text.trim() });
      if (andApprove) await api.post(`/clinical-suggestions/${row.id}/approve`, {});
      onDone();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409 && /Merge/.test(errorMessage(e))) {
        const found = await api.get<View>(`/clinical-suggestions/all?list=${row.list}&status=APPROVED&q=${encodeURIComponent(text.trim())}&limit=20`)
          .catch(() => null);
        setTwin(found?.items.find((r) => r.text.trim().toLowerCase() === text.trim().toLowerCase()) ?? null);
      }
      setError(errorMessage(e));
      setBusy(false);
    }
  };
  const merge = async () => {
    if (!twin) return;
    setBusy(true); setError(null);
    try { await api.post(`/clinical-suggestions/${row.id}/merge`, { intoId: twin.id }); onDone(); }
    catch (e) { setError(errorMessage(e)); setBusy(false); }
  };
  const valid = text.trim().length >= 2 && text.trim().length <= 80;
  return (
    <Dialog title={pending ? 'Edit before approving' : 'Edit suggestion'} onClose={onClose}
      footer={<>
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        {pending && <Button variant="secondary" loading={busy} disabled={!valid} onClick={() => void save(false)}>Save only</Button>}
        <Button loading={busy} disabled={!valid || (!pending && text.trim() === row.text)} onClick={() => void save(pending)}>
          {pending ? 'Save and approve' : 'Save'}
        </Button>
      </>}>
      <div className="space-y-3">
        <TextField label="Wording" value={text} onChange={setText} hint="2 to 80 characters. Changes what is offered from now on; nothing already recorded changes." />
        {error && <p role="alert" className="text-caption text-danger-600">{error}</p>}
        {twin && (
          <div className="flex flex-wrap items-center gap-2 rounded-input bg-neutral-100 px-3 py-2 text-small text-neutral-700">
            <span className="min-w-0 flex-1">Merge into “{twin.text}”? This one is taken off the list and its uses are counted there.</span>
            <Button size="sm" loading={busy} onClick={() => void merge()}>Merge</Button>
          </div>
        )}
      </div>
    </Dialog>
  );
}
